import { type VariantProps, cva } from "class-variance-authority";
import clsx from "clsx";
import { FieldError, TextField } from "react-aria-components";
import type { ControllerRenderProps } from "react-hook-form";

import { uiOutlineClass } from "@/components/ui/global/outline";
import { InputHeader, type InputHeaderProps } from "@/components/ui/inputs/shared/InputHeader";
import { Typography } from "@/components/ui/text/Typography/Typography";

export const textInput = cva(
  [
    "w-full",
    "rounded-2xs",
    "bg-elevation-fill-default-1",
    "px-input-side-spacing",
    "font-primary",
    "font-weight-400",
    "text-secondary-50",
    "placeholder:text-text-default-3",
    uiOutlineClass,
    "focus-visible:outline-hidden!",
    "invalid:border invalid:border-danger",
    "focus:border focus:border-white",
    "focus-within:border focus-within:border-white",
    "focus-visible:outline-secondary-200",
    "hover:border hover:border-elevation-outline-default-2",
    "!outline-hidden! focus-within:outline-hidden!",
    "t:font-weight-400 text-label-1",
  ],
  {
    variants: {
      variant: {
        outlined: ["border border-secondary-75", "py-height-s"],
        filled: ["border border-none bg-neutral-50 focus:bg-neutral-400", "py-height-s"],
        inline: ["border border-transparent", "py-height-xs"],
        chat: [
          "transition-colors duration-150",
          "rounded-s",
          "placeholder:align-middle",
          "border-none",
          "outline-hidden!",
          "focus:border-none",
          "focus-visible:outline-hidden!",
          "h-auto",
          "hover:bg-neutral-50",
          "bg-neutral-50",
          "active:bg-secondary-400",
          "resize-none",
          "min-h-10",
          "max-h-20",
          "placeholder:text-neutral-200",
          "shadow-[0_1px_4px_0_rgba(0,0,0,0.30)_inset]",
          "!pt-2-5",
        ],
        aiChat: [
          "transition-colors duration-150",
          "border-none",
          "!outline-hidden",
          "focus:border-none",
          "focus-visible:!outline-hidden",
          "h-auto",
          "hover:bg-neutral-50",
          "bg-neutral-50",
          "active:bg-secondary-400",
          "resize-none",
          "min-h-20",
          "max-h-32",
        ],
        login: [
          "bg-secondary-50",
          "border",
          "py-height-s",
          "border border-secondary-75",
          "placeholder:text-secondary-200",
          "focus:!border focus:border-white",
          "hover:border hover:border-neutral-75",
          "invalid:border-none",
        ],
        search: [
          "!rounded-full border border-input-outlined-outline-idle bg-interactive-outlined-secondary-idle!",
          "py-input-height-default!",
          "text-label-1",
          "placeholder:font-weight-400! placeholder:text-label-1 placeholder:text-text-default-3",
        ],
        watchlist: [
          "!bg-secondary-400 !py-0-5 !pl-1-5 !text-body-paragraph-s !text-neutral-75 !border !border-white !pr-0 w-full flex-1",
        ],
      },
    },
    defaultVariants: {
      variant: "outlined",
    },
  },
);

export type TextInputVariantProps = VariantProps<typeof textInput>;

export type TextInputWrapperProps = InputHeaderProps & {
  error?: string | React.ReactNode;
  children: React.ReactNode;
  className?: string;
  textfieldClassName?: string;
  field?: Omit<ControllerRenderProps, "ref">;
  variant?: TextInputVariantProps["variant"];
};

export const TextInputWrapper = ({
  label,
  tooltipText,
  helperText,
  required,
  error,
  className,
  children,
  hideLabels,
  hideLabel,
  textfieldClassName,
  variant,
  ...props
}: TextInputWrapperProps) => {
  return (
    <div className={clsx("w-full", className)}>
      <TextField
        {...props.field}
        isDisabled={props.field?.disabled}
        isInvalid={!!error}
        className={textfieldClassName}
      >
        <InputHeader
          label={label}
          tooltipText={tooltipText}
          helperText={helperText}
          required={required}
          hideLabels={hideLabel}
        />
        {children}
        {error && (
          <Typography
            className={clsx(
              "mt-2 font-weight-400 text-danger",
              hideLabels && "hidden",
              variant === "login" && "leading-5",
            )}
            size="labels-s"
          >
            <FieldError>{error}</FieldError>
          </Typography>
        )}
      </TextField>
    </div>
  );
};
